(() => {
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const sections = document.querySelectorAll('[data-blog-explorer]');
  if (!sections.length) return;

  sections.forEach((section) => {
    const grid = section.querySelector('[data-blog-explorer-grid]');
    const categorySelect = section.querySelector('[data-category-select]');
    const subcategorySelect = section.querySelector('[data-subcategory-select]');
    const searchInput = section.querySelector('[data-search-input]');
    const searchInputMobile = section.querySelector('[data-search-input-mobile]');
    const sortSelect = section.querySelector('[data-sort-select]');
    const emptyState = section.querySelector('[data-empty-state]');
    const skeletons = section.querySelector('[data-skeletons]');
    const pagination = section.querySelector('[data-pagination]');
    const loadMoreWrapper = section.querySelector('[data-load-more-wrapper]');
    const loadMoreButton = section.querySelector('[data-load-more]');
    const filterDrawer = section.querySelector('[data-filter-drawer]');
    const sortDrawer = section.querySelector('[data-sort-drawer]');
    const openFilters = section.querySelector('[data-open-filters]');
    const openSort = section.querySelector('[data-open-sort]');
    const activeFilters = section.querySelector('[data-active-filters]');

    const paginationMode = section.dataset.paginationMode || 'load_more_button';
    const blogHandle = section.dataset.blogHandle || '';
    const categorySource = section.dataset.categorySource || 'tags';
    const showAuthor = section.dataset.showAuthor === 'true';
    const showDate = section.dataset.showDate === 'true';
    const showExcerpt = section.dataset.showExcerpt === 'true';
    const showReadTime = section.dataset.showReadTime === 'true';
    const excerptLength = parseInt(section.dataset.excerptLength || '0', 10) || 0;
    const allTags = (section.dataset.allTags || '')
      .split('||')
      .map((tag) => tag.trim())
      .filter(Boolean);
    const perPage = parseInt(section.dataset.articlesPerPage || '0', 10) || 0;

    let currentPage = parseInt(pagination?.dataset.currentPage || '1', 10);
    let totalPages = parseInt(pagination?.dataset.totalPages || '1', 10);
    let cachedCards = null;
    let cachedTotalPages = null;

    const collectCards = () => Array.from(grid.querySelectorAll('.sas-blog-explorer-card'));

    const applyVisibility = (cards = collectCards()) => {
      cards.forEach((card) => {
        const excerpt = card.querySelector('.sas-blog-explorer-card__excerpt');
        const author = card.querySelector('.sas-blog-explorer-card__author');
        const date = card.querySelector('.sas-blog-explorer-card__date');
        const readTime = card.querySelector('.sas-blog-explorer-card__read-time');

        if (excerpt) excerpt.style.display = showExcerpt ? '' : 'none';
        if (author) author.style.display = showAuthor ? '' : 'none';
        if (date) date.style.display = showDate ? '' : 'none';
        if (readTime) readTime.style.display = showReadTime ? '' : 'none';
      });
    };

    const truncateWords = (text, limit) => {
      if (!limit) return text;
      const words = text.split(/\s+/).filter(Boolean);
      if (words.length <= limit) return text;
      return `${words.slice(0, limit).join(' ')}…`;
    };

    const applyExcerptLength = (cards = collectCards()) => {
      if (!excerptLength) return;
      cards.forEach((card) => {
        const full = (card.dataset.excerptFull || card.dataset.excerpt || '').toString();
        const truncated = truncateWords(full, excerptLength);
        const excerptEl = card.querySelector('.sas-blog-explorer-card__excerpt');
        if (excerptEl) excerptEl.textContent = truncated;
        card.dataset.excerpt = truncated;
      });
    };

    const getSearchValue = () => {
      const mobileValue = searchInputMobile ? searchInputMobile.value.trim() : '';
      if (mobileValue) return mobileValue;
      return searchInput ? searchInput.value.trim() : '';
    };

    const setSearchValue = (value) => {
      if (searchInput) searchInput.value = value;
      if (searchInputMobile) searchInputMobile.value = value;
    };

    const applyFilters = () => {
      const cards = collectCards();
      const categoryValue = categorySelect ? categorySelect.value.toLowerCase() : '';
      const subcategoryValue = subcategorySelect ? subcategorySelect.value.toLowerCase() : '';
      const searchValue = getSearchValue().toLowerCase();

      let visibleCount = 0;

      cards.forEach((card) => {
        const title = (card.dataset.title || '').toLowerCase();
        const excerpt = (card.dataset.excerpt || '').toLowerCase();
        const tags = (card.dataset.tags || '').toLowerCase();
        const category = (card.dataset.category || '').toLowerCase();
        const subcategory = (card.dataset.subcategory || '').toLowerCase();
        const categoryList = category
          ? category.split(',').map((item) => item.trim()).filter(Boolean)
          : [];

        let matches = true;

        if (categoryValue && !categoryList.includes(categoryValue)) {
          matches = false;
        }

        if (subcategoryValue && subcategory !== subcategoryValue) {
          matches = false;
        }

        if (searchValue) {
          const haystack = `${title} ${excerpt} ${tags}`;
          if (!haystack.includes(searchValue)) {
            matches = false;
          }
        }

        card.classList.toggle('is-hidden', !matches);
        if (matches) visibleCount += 1;
      });

      if (emptyState) {
        emptyState.hidden = visibleCount !== 0;
      }
      renderActiveFilters();
    };

    const renderActiveFilters = () => {
      if (!activeFilters) return;
      const chips = [];

      if (categorySelect?.value) {
        const selected = categorySelect.options[categorySelect.selectedIndex];
        chips.push({ type: 'category', label: selected?.textContent || categorySelect.value });
      }
      if (subcategorySelect?.value) {
        const selected = subcategorySelect.options[subcategorySelect.selectedIndex];
        chips.push({ type: 'subcategory', label: selected?.textContent || subcategorySelect.value });
      }
      if (getSearchValue()) {
        chips.push({ type: 'search', label: getSearchValue() });
      }

      activeFilters.innerHTML = '';
      if (chips.length === 0) {
        activeFilters.hidden = true;
        return;
      }

      chips.forEach((chip) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'sas-blog-explorer-filter-chip';
        el.innerHTML = `<span>${chip.label}</span><span class="blog-explorer-filter-chip__x">×</span>`;
        el.addEventListener('click', () => {
          if (chip.type === 'category' && categorySelect) {
            categorySelect.value = '';
            categorySelect.dispatchEvent(new Event('change'));
          }
          if (chip.type === 'subcategory' && subcategorySelect) {
            subcategorySelect.value = '';
            subcategorySelect.dispatchEvent(new Event('change'));
          }
          if (chip.type === 'search') {
            setSearchValue('');
            if (searchInput) searchInput.dispatchEvent(new Event('input'));
            if (searchInputMobile) searchInputMobile.dispatchEvent(new Event('input'));
          }
        });
        activeFilters.appendChild(el);
      });

      activeFilters.hidden = false;
    };

    const buildCategories = () => {
      if (!categorySelect) return;
      if (categorySelect.options.length > 1) return;
      const categories = new Map();

      if (categorySource === 'tags' && allTags.length > 0) {
        allTags.forEach((tag) => {
          const parts = tag.split('/');
          const main = parts[0].trim();
          if (!main) return;
          const key = main.toLowerCase();
          if (!categories.has(key)) categories.set(key, main);
        });
      } else {
        const cards = collectCards();
        cards.forEach((card) => {
          const raw = (card.dataset.category || '').toString();
          raw
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
            .forEach((item) => {
              const key = item.toLowerCase();
              if (!categories.has(key)) categories.set(key, item);
            });
        });
      }

      const currentValue = categorySelect.value;
      categorySelect.innerHTML = '<option value="">All</option>';
      Array.from(categories.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .forEach(([key, label]) => {
          const option = document.createElement('option');
          option.value = key;
          option.textContent = label;
          categorySelect.appendChild(option);
        });
      if (currentValue) categorySelect.value = currentValue;
    };

    const buildSubcategories = () => {
      if (!subcategorySelect) return;
      const selectedCategory = categorySelect ? categorySelect.value.toLowerCase() : '';

      const subs = new Set();
      if (categorySource === 'tags' && allTags.length > 0) {
        allTags.forEach((tag) => {
          if (!tag.includes('/')) return;
          const parts = tag.split('/');
          const main = parts[0].trim().toLowerCase();
          const sub = parts.slice(1).join('/').trim().toLowerCase();
          if (!sub) return;
          if (selectedCategory && main !== selectedCategory) return;
          subs.add(sub);
        });
      } else {
        const cards = collectCards();
        cards.forEach((card) => {
          const category = (card.dataset.category || '').toLowerCase();
          const sub = (card.dataset.subcategory || '').toLowerCase();
          if (!sub) return;
          if (selectedCategory && category !== selectedCategory) return;
          subs.add(sub);
        });
      }

      const currentValue = subcategorySelect.value;
      subcategorySelect.innerHTML = '<option value="">All</option>';
      Array.from(subs)
        .sort()
        .forEach((sub) => {
          const option = document.createElement('option');
          option.value = sub;
          option.textContent = sub;
          subcategorySelect.appendChild(option);
        });
      if (currentValue) subcategorySelect.value = currentValue;
    };

    const setDrawerPanel = (drawer, panelName = 'main') => {
      if (!drawer) return;
      drawer.querySelectorAll('[data-drawer-panel]').forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.drawerPanel === panelName);
      });
    };

    const openDrawer = (drawer) => {
      if (!drawer) return;
      if (window.matchMedia('(min-width: 750px)').matches) return;
      if (drawer === filterDrawer && sortDrawer) sortDrawer.classList.remove('is-open');
      if (drawer === sortDrawer && filterDrawer) filterDrawer.classList.remove('is-open');
      drawer.classList.add('is-open');
      setDrawerPanel(drawer, 'main');
      if (drawer === filterDrawer) {
        updateCategoryActive();
        if (filterDrawer) {
          filterDrawer
            .querySelectorAll('[data-subcategory-accordion] .sas-blog-explorer-drawer__choice')
            .forEach((el) => {
              const isActive =
                el.textContent?.toLowerCase() === subcategorySelect?.value.toLowerCase();
              el.classList.toggle('is-active', isActive);
              el.classList.toggle('active', isActive);
            });
        }
      }
      document.body.classList.add('blog-explorer-drawer-open');
    };

    const closeDrawer = (drawer) => {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      setDrawerPanel(drawer, 'main');
      if (!filterDrawer?.classList.contains('is-open') && !sortDrawer?.classList.contains('is-open')) {
        document.body.classList.remove('blog-explorer-drawer-open');
      }
    };

    const updateCategoryActive = () => {
      const list = filterDrawer?.querySelector('[data-category-list]');
      if (!list || !categorySelect) return;
      const currentValue = categorySelect.value.toLowerCase().trim();
      list.querySelectorAll('.sas-blog-explorer-drawer__choice').forEach((el) => {
        const value = (el.dataset.value || '').toLowerCase().trim();
        const isActive = value === currentValue;
        el.classList.toggle('is-active', isActive);
        el.classList.toggle('active', isActive);
        if (isActive) {
          el.setAttribute('data-selected', 'true');
        } else {
          el.removeAttribute('data-selected');
        }
      });
    };

    const buildCategoryList = () => {
      const list = filterDrawer?.querySelector('[data-category-list]');
      if (!list || !categorySelect) return;
      list.innerHTML = '';
      Array.from(categorySelect.options)
        .filter((opt) => opt.value !== '')
        .forEach((opt) => {
          const item = document.createElement('li');
          item.className = 'sas-blog-explorer-drawer__list-item';
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'sas-blog-explorer-drawer__choice';
          button.textContent = opt.textContent || opt.value;
          button.dataset.value = opt.value;
          if (opt.value.toLowerCase() === categorySelect.value.toLowerCase()) {
            button.classList.add('is-active', 'active');
            button.setAttribute('data-selected', 'true');
          }
          button.addEventListener('click', () => {
            categorySelect.value = opt.value;
            categorySelect.dispatchEvent(new Event('change'));
            list.querySelectorAll('.sas-blog-explorer-drawer__choice').forEach((el) => {
              el.classList.toggle('is-active', el === button);
              el.classList.toggle('active', el === button);
              if (el === button) {
                el.setAttribute('data-selected', 'true');
              } else {
                el.removeAttribute('data-selected');
              }
            });
          });
          item.appendChild(button);
          list.appendChild(item);
        });
      updateCategoryActive();
    };

    const buildSubcategoryAccordion = () => {
      const accordion = filterDrawer?.querySelector('[data-subcategory-accordion]');
      if (!accordion || !subcategorySelect) return;
      accordion.innerHTML = '';

      const groups = new Map();
      const selectedCategory = categorySelect ? categorySelect.value.toLowerCase() : '';

      if (categorySource === 'tags' && allTags.length > 0) {
        allTags.forEach((tag) => {
          if (!tag.includes('/')) return;
          const parts = tag.split('/');
          const main = parts[0].trim();
          const sub = parts.slice(1).join('/').trim();
          if (!main || !sub) return;
          const key = main.toLowerCase();
          if (selectedCategory && key !== selectedCategory) return;
          if (!groups.has(key)) groups.set(key, { label: main, items: new Map() });
          const subKey = sub.toLowerCase();
          if (!groups.get(key).items.has(subKey)) groups.get(key).items.set(subKey, sub);
        });
      } else {
        const cards = collectCards();
        cards.forEach((card) => {
          const categoryRaw = (card.dataset.category || '').toString();
          const subRaw = (card.dataset.subcategory || '').toString();
          if (!categoryRaw || !subRaw) return;
          const mains = categoryRaw.split(',').map((item) => item.trim()).filter(Boolean);
          mains.forEach((main) => {
            const key = main.toLowerCase();
            if (selectedCategory && key !== selectedCategory) return;
            if (!groups.has(key)) groups.set(key, { label: main, items: new Map() });
            subRaw
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)
              .forEach((item) => {
                const subKey = item.toLowerCase();
                if (!groups.get(key).items.has(subKey)) groups.get(key).items.set(subKey, item);
              });
          });
        });
      }

      Array.from(groups.values())
        .sort((a, b) => a.label.localeCompare(b.label))
        .forEach((group) => {
          const sectionEl = document.createElement('div');
          sectionEl.className = 'sas-blog-explorer-accordion__section';
          const header = document.createElement('button');
          header.type = 'button';
          header.className = 'sas-blog-explorer-accordion__header';
          header.innerHTML = `<span>${group.label}</span><span class="blog-explorer-accordion__chev">›</span>`;
          header.addEventListener('click', () => {
            sectionEl.classList.toggle('is-open');
          });

          const body = document.createElement('div');
          body.className = 'sas-blog-explorer-accordion__body';
          Array.from(group.items.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .forEach(([subKey, subLabel]) => {
              const button = document.createElement('button');
              button.type = 'button';
              button.className = 'sas-blog-explorer-drawer__choice';
              button.textContent = subLabel;
              if (subKey === subcategorySelect.value.toLowerCase()) {
                button.classList.add('is-active');
              }
              button.addEventListener('click', () => {
                subcategorySelect.value = subKey;
                subcategorySelect.dispatchEvent(new Event('change'));
                body.querySelectorAll('.sas-blog-explorer-drawer__choice').forEach((el) => {
                  el.classList.toggle('is-active', el === button);
                });
              });
              body.appendChild(button);
            });

          sectionEl.appendChild(header);
          sectionEl.appendChild(body);
          accordion.appendChild(sectionEl);
        });
    };

    const buildSortList = () => {
      const list = sortDrawer?.querySelector('[data-sort-list]');
      if (!list || !sortSelect) return;
      list.innerHTML = '';
      Array.from(sortSelect.options).forEach((opt) => {
        const item = document.createElement('li');
        item.className = 'sas-blog-explorer-drawer__list-item';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sas-blog-explorer-drawer__choice';
        button.textContent = opt.textContent || opt.value;
        if (opt.value === sortSelect.value) button.classList.add('is-active');
        button.addEventListener('click', () => {
          sortSelect.value = opt.value;
          sortSelect.dispatchEvent(new Event('change'));
          closeDrawer(sortDrawer);
        });
        item.appendChild(button);
        list.appendChild(item);
      });
    };

    const sortCards = () => {
      if (!sortSelect) return;
      const mode = sortSelect.value;
      const cards = collectCards();

      const sorted = cards.slice().sort((a, b) => {
        if (mode === 'alpha') {
          return (a.dataset.title || '').localeCompare(b.dataset.title || '');
        }
        if (mode === 'oldest') {
          return (a.dataset.date || 0) - (b.dataset.date || 0);
        }
        if (mode === 'trending') {
          return (parseFloat(b.dataset.score || '0') - parseFloat(a.dataset.score || '0'));
        }
        return (b.dataset.date || 0) - (a.dataset.date || 0);
      });

      sorted.forEach((card) => grid.appendChild(card));
    };

    const updatePaginationUI = () => {
      if (!pagination) return;
      pagination.innerHTML = '';
      if (totalPages <= 1 || paginationMode !== 'number_pagination') {
        pagination.style.display = 'none';
        return;
      }
      pagination.style.display = 'flex';

      for (let i = 1; i <= totalPages; i += 1) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = i;
        if (i === currentPage) btn.classList.add('is-active');
        btn.addEventListener('click', () => loadPage(i, false));
        pagination.appendChild(btn);
      }
    };

    const setLoading = (state) => {
      section.classList.toggle('is-loading', state);
      if (skeletons) skeletons.hidden = !state;
    };

    const setLoadMoreLoading = (state) => {
      if (!loadMoreWrapper || !loadMoreButton) return;
      loadMoreWrapper.classList.toggle('is-loading', state);
      loadMoreButton.disabled = state;
      loadMoreButton.setAttribute('aria-busy', state ? 'true' : 'false');
    };

    const toggleLoadMore = () => {
      if (!loadMoreWrapper) return;
      if (paginationMode === 'number_pagination') {
        loadMoreWrapper.style.display = 'none';
        return;
      }
      if (currentPage >= totalPages) {
        loadMoreWrapper.style.display = 'none';
      } else {
        loadMoreWrapper.style.display = 'flex';
      }
    };

    const renderAllCached = () => {
      if (!cachedCards) return;
      grid.innerHTML = '';
      cachedCards.forEach((card) => grid.appendChild(card));
      currentPage = 1;
      totalPages = 1;
      if (pagination) {
        pagination.dataset.currentPage = currentPage;
        pagination.dataset.totalPages = totalPages;
      }
      applyExcerptLength(cachedCards);
      applyVisibility(cachedCards);
      sortCards();
      applyFilters();
      updatePaginationUI();
      toggleLoadMore();
    };

    const renderPageFromCache = (page, append) => {
      if (!cachedCards) return;
      const start = perPage > 0 ? (page - 1) * perPage : 0;
      const end = perPage > 0 ? start + perPage : cachedCards.length;
      const pageCards = cachedCards.slice(start, end);

      let insertedCards = pageCards;
      if (append) {
        const existingUrls = new Set(
          Array.from(grid.querySelectorAll('.sas-blog-explorer-card a.sas-blog-explorer-card__media'))
            .map((link) => link.getAttribute('href'))
            .filter(Boolean)
        );
        const cardsToInsert = pageCards.filter((card) => {
          const link = card.querySelector('a.sas-blog-explorer-card__media');
          const href = link ? link.getAttribute('href') : '';
          if (!href) return true;
          if (existingUrls.has(href)) return false;
          existingUrls.add(href);
          return true;
        });
        cardsToInsert.forEach((card) => grid.appendChild(card));
        insertedCards = cardsToInsert;
      } else {
        grid.innerHTML = '';
        pageCards.forEach((card) => grid.appendChild(card));
      }

      applyExcerptLength(insertedCards);
      applyVisibility(insertedCards);
      currentPage = page;
      totalPages = cachedTotalPages || totalPages;
      if (pagination) {
        pagination.dataset.currentPage = currentPage;
        pagination.dataset.totalPages = totalPages;
      }

      buildCategories();
      buildSubcategories();
      sortCards();
      applyFilters();
      updatePaginationUI();
      toggleLoadMore();
    };

    const loadAllCards = async () => {
      if (!blogHandle || cachedCards) return;
      setLoading(true);
      try {
        const response = await fetch(`/blogs/${blogHandle}?view=ajax`, {
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Network error');
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const ajax = doc.querySelector('.sas-blog-explorer-ajax');
        if (!ajax) throw new Error('Invalid response');
        cachedCards = Array.from(ajax.querySelectorAll('.sas-blog-explorer-card'));
        cachedTotalPages = parseInt(ajax.dataset.totalPages || totalPages, 10);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    const refreshForFilters = async () => {
      const hasFilters = Boolean(
        (categorySelect && categorySelect.value) ||
          (subcategorySelect && subcategorySelect.value) ||
          getSearchValue()
      );

      if (hasFilters) {
        if (!cachedCards) {
          await loadAllCards();
        }
        renderAllCached();
      } else {
        if (cachedCards) {
          renderPageFromCache(1, false);
        } else {
          applyFilters();
          updatePaginationUI();
          toggleLoadMore();
        }
        sortCards();
      }
    };

    const loadPage = async (page, append) => {
      if (!blogHandle) return;
      if (page > totalPages) return;

      if (cachedCards) {
        renderPageFromCache(page, append);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(`/blogs/${blogHandle}?page=${page}&view=ajax`, {
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Network error');
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const ajax = doc.querySelector('.sas-blog-explorer-ajax');
        if (!ajax) throw new Error('Invalid response');

        cachedCards = Array.from(ajax.querySelectorAll('.sas-blog-explorer-card'));
        cachedTotalPages = parseInt(ajax.dataset.totalPages || totalPages, 10);
        renderPageFromCache(page, append);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (categorySelect) {
      categorySelect.addEventListener('change', () => {
        const activePanel =
          filterDrawer?.querySelector('[data-drawer-panel].is-active')?.dataset.drawerPanel || 'main';
        buildSubcategories();
        refreshForFilters();
        buildSubcategoryAccordion();
        updateCategoryActive();
        setDrawerPanel(filterDrawer, activePanel);
      });
    }

    if (subcategorySelect) {
      subcategorySelect.addEventListener('change', () => {
        refreshForFilters();
        if (filterDrawer) {
          filterDrawer
            .querySelectorAll('[data-subcategory-accordion] .sas-blog-explorer-drawer__choice')
            .forEach((el) => {
              const isActive = el.textContent?.toLowerCase() === subcategorySelect.value.toLowerCase();
              el.classList.toggle('is-active', isActive);
              el.classList.toggle('active', isActive);
            });
        }
      });
    }

    if (searchInput) {
      searchInput.addEventListener('input', debounce(() => {
        if (searchInputMobile) searchInputMobile.value = searchInput.value;
        refreshForFilters();
      }));
    }
    if (searchInputMobile) {
      searchInputMobile.addEventListener('input', debounce(() => {
        if (searchInput) searchInput.value = searchInputMobile.value;
        refreshForFilters();
      }));
    }

    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        sortCards();
        refreshForFilters();
        buildSortList();
      });
    }

    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', async () => {
        setLoadMoreLoading(true);
        await loadPage(currentPage + 1, true);
        setLoadMoreLoading(false);
      });
    }

    if (paginationMode === 'infinite_scroll' && loadMoreWrapper) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadPage(currentPage + 1, true);
          }
        });
      });
      observer.observe(loadMoreWrapper);
    }

    if (openFilters) {
      openFilters.addEventListener('click', () => openDrawer(filterDrawer));
    }
    if (openSort) {
      openSort.addEventListener('click', () => openDrawer(sortDrawer));
    }
    if (filterDrawer) {
      filterDrawer.querySelectorAll('[data-open-panel]').forEach((btn) => {
        btn.addEventListener('click', () => setDrawerPanel(filterDrawer, btn.dataset.openPanel));
      });
      filterDrawer.querySelectorAll('[data-back-panel]').forEach((btn) => {
        btn.addEventListener('click', () => setDrawerPanel(filterDrawer, btn.dataset.backPanel));
      });
    }
    [filterDrawer, sortDrawer].forEach((drawer) => {
      if (!drawer) return;
      drawer.querySelectorAll('[data-close-drawer]').forEach((btn) => {
        btn.addEventListener('click', () => closeDrawer(drawer));
      });
    });
    if (filterDrawer) {
      const clearBtn = filterDrawer.querySelector('[data-clear-filters]');
      const applyBtn = filterDrawer.querySelector('[data-apply-filters]');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => {
          if (categorySelect) categorySelect.value = '';
          if (subcategorySelect) subcategorySelect.value = '';
          setSearchValue('');
          buildSubcategories();
          refreshForFilters();
          buildCategoryList();
          buildSubcategoryAccordion();
          setDrawerPanel(filterDrawer, 'main');
        });
      }
      if (applyBtn) {
        applyBtn.addEventListener('click', () => {
          setDrawerPanel(filterDrawer, 'main');
          closeDrawer(filterDrawer);
        });
      }
    }
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeDrawer(filterDrawer);
        closeDrawer(sortDrawer);
      }
    });

    applyVisibility();
    applyExcerptLength();
    buildCategories();
    buildSubcategories();
    buildCategoryList();
    updateCategoryActive();
    buildSubcategoryAccordion();
    buildSortList();
    sortCards();
    refreshForFilters();
    updatePaginationUI();
    toggleLoadMore();

    if (collectCards().length === 0 && blogHandle) {
      loadPage(1, false);
    }
  });
})();
